/**
 * Converts a date input into a human-readable string.
 *
 * @param {string | number | Date} dateInput - The date value to format (string, timestamp, or Date object).
 * @param {boolean} includeTime - Whether to include time (hours and minutes). Default is false.
 * @param {Object} formatOptions - Intl.DateTimeFormat options to customize the output.
 * @param {string} locale - Optional locale string. Defaults to browser locale.
 * @param {boolean} use24Hour - Whether to use 24-hour format (true) or 12-hour with AM/PM (false). Default is true.
 * @returns {string} A formatted date string, or "N/F" if input is invalid.
 */
export function humanReadableDate(
  dateInput,
  includeTime = false,
  formatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  },
  locale = navigator.language,
  use24Hour = false,
) {
  if (
    !dateInput ||
    dateInput === "" ||
    dateInput === "null" ||
    dateInput === "undefined"
  ) {
    return "N/F";
  }

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const finalOptions = {
    ...formatOptions,
    ...(includeTime && {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: use24Hour ? "h23" : "h12",
    }),
  };

  return date.toLocaleString(locale, finalOptions);
}

/**
 * Standard date formatter used across the recruitment module.
 */
export const formatDate = (date, includeTime = false) => {
  return humanReadableDate(date, includeTime);
};
